import { call, put, select, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../config/app';
import { endpoints, routes } from '../../constants/routing';
import { getInitialTemplateBuilderValues } from '../../constants/templates';
import { IResponse } from '../../types/http';
import { IReduxAction } from '../../types/redux';
import { IApiError } from '../../types/shell';
import {
  IClaimLinkBuilderValues,
  IClaimLinkTemplate,
  IClaimLinkTemplateErrorResponse,
  ICreateClaimLinkTemplateResponse,
  IFetchClaimLinkTemplatesPayload,
  IFetchClaimLinkTemplatesResponse,
  ISaveClaimLinkTemplateRequest,
  IUpdateClaimLinkTemplateDetailsRequest,
  TemplateTypesEnum,
  TemplateValidationModalEnum,
} from '../../types/templates';
import { buildEndpointRoute } from '../../utils/routing';
import { isTemplateEmail } from '../../utils/templates';
import { routerProxyPush } from '../actions/shell';
import * as TemplatesActions from '../actions/templates';
import { selectOrganizationInfo } from '../selectors/auth';
import { selectCurrentOrganizationLogoUrl } from '../selectors/organizations';
import { selectClaimLinkLastFetchParams, selectClaimLinks } from '../selectors/templates';
import { AuthRequest } from './helpers';

export function* emailsTemplatesWorkerSaga(): Generator<any, any, any> {
  try {
    const endpoint = `${monolithApiUrl}${endpoints.emails}`;

    const response = yield call<any>(AuthRequest, { endpoint });
    if (!response.ok) {
      yield put(TemplatesActions.fetchEmailsTemplatesFailure(response.body));
    } else {
      yield put(TemplatesActions.fetchEmailsTemplatesSuccess(response.body));
    }
  } catch (error) {
    yield put(TemplatesActions.fetchEmailsTemplatesFailure(error));
  }
}

export function* notesTemplatesWorkerSaga(): Generator<any, any, any> {
  try {
    const endpoint = `${monolithApiUrl}${endpoints.notes}`;

    const response = yield call<any>(AuthRequest, { endpoint });
    if (!response.ok) {
      yield put(TemplatesActions.fetchNotesTemplatesFailure(response.body));
    } else {
      yield put(TemplatesActions.fetchNotesTemplatesSuccess(response.body));
    }
  } catch (error) {
    yield put(TemplatesActions.fetchNotesTemplatesFailure(error));
  }
}

export function* claimLinkTemplatesWorkerSaga(
  action: IReduxAction<IFetchClaimLinkTemplatesPayload>,
): Generator<any, any, any> {
  const { page_size, department_ids, page, status, created_by, search_query, org_id, resolve, reject } =
    action.payload || {};

  try {
    const orgInfo: ReturnType<typeof selectOrganizationInfo> = yield select(selectOrganizationInfo);
    const endpoint = `${monolithApiUrl}${endpoints.getClaimLinkTemplates}`;
    const body = JSON.stringify({
      // org_id override is used by super admins editing other orgs; backend enforces authorization
      org_id: org_id || orgInfo?.uid,
      page_size,
      page,
      ...(department_ids?.length ? { department_ids } : {}),
      ...(search_query ? { search_query } : {}),
      ...(status ? { status } : {}),
      ...(created_by ? { created_by } : {}),
    });

    const response: IResponse<IFetchClaimLinkTemplatesResponse | IApiError> = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
      body,
    });

    if (!response.ok) {
      yield put(TemplatesActions.fetchClaimLinkTemplatesFailure(response.body));
      if (reject) {
        yield call(reject, response.body);
      }
    } else {
      yield put(TemplatesActions.fetchClaimLinkTemplatesSuccess(response.body as IFetchClaimLinkTemplatesResponse));
      if (resolve) {
        yield call(resolve, response.body);
      }
    }
  } catch (error) {
    yield put(TemplatesActions.fetchClaimLinkTemplatesFailure(error));
    if (reject) {
      yield call(reject, error);
    }
  }
}

function* fetchClaimLinkTemplateByIdWorkerSaga(action: IReduxAction<{ id: string }>): Generator<any, any, any> {
  const { id } = action.payload || {};

  if (!id) {
    yield put(TemplatesActions.fetchClaimLinkTemplateByIdFailure(new Error('Template id is required')));
    return;
  }

  try {
    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.getClaimLinkTemplateById, { id })}`;
    const response: IResponse<IClaimLinkTemplate | IApiError> = yield call(AuthRequest, { endpoint, method: 'GET' });
    if (!response.ok) {
      yield put(TemplatesActions.fetchClaimLinkTemplateByIdFailure(response.body));
    } else {
      yield put(TemplatesActions.fetchClaimLinkTemplateByIdSuccess(response.body as IClaimLinkTemplate));
    }
  } catch (error) {
    yield put(TemplatesActions.fetchClaimLinkTemplateByIdFailure(error));
  }
}

function* updateClaimLinkTemplateDetailsWorkerSaga(
  action: IReduxAction<IUpdateClaimLinkTemplateDetailsRequest>,
): Generator<any, any, any> {
  const { uid, status, is_default, name, department_ids, resolve, reject } = action.payload || {};

  try {
    if (!uid) {
      throw new Error('Template ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.updateClaimLinkTemplateDetails, { id: uid })}`;

    const body = JSON.stringify({
      ...(status !== undefined ? { status } : {}),
      ...(is_default !== undefined ? { is_default } : {}),
      ...(name !== undefined ? { name } : {}),
      ...(department_ids !== undefined
        ? { department_ids: department_ids.length === 0 ? ['no_department'] : department_ids }
        : {}),
    });

    const response: IResponse<any | IApiError> = yield call(AuthRequest, {
      endpoint,
      method: 'PATCH',
      body,
    });

    if (!response.ok) {
      yield put(TemplatesActions.updateClaimLinkTemplateDetailsFailure(response.body as IApiError));
      if (reject) {
        yield call(reject, response.body);
      }
    } else {
      yield put(
        TemplatesActions.updateClaimLinkTemplateDetailsSuccess({ uid, status, is_default, name, department_ids }),
      );
      const lastFetchParams: ReturnType<typeof selectClaimLinkLastFetchParams> = yield select(
        selectClaimLinkLastFetchParams,
      );
      if (lastFetchParams) {
        yield put(
          TemplatesActions.fetchClaimLinkTemplatesRequest({
            ...lastFetchParams,
            silent: true,
          } as IFetchClaimLinkTemplatesPayload),
        );
      }
      if (resolve) {
        yield call(resolve);
      }
    }
  } catch (error) {
    yield put(TemplatesActions.updateClaimLinkTemplateDetailsFailure(error));
    if (reject) {
      yield call(reject, error);
    }
  }
}

export function* addTemplateWorkerSaga(action: any): Generator<any, any, any> {
  const {
    template: { templateType, ...rest },
    resolve,
    reject,
  } = action.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.createTemplate}`;
    const response = yield call<any>(AuthRequest, {
      endpoint,
      method: 'POST',
      body: JSON.stringify({
        [isTemplateEmail(templateType) ? 'email_templates' : 'message_templates']: [rest],
      }),
    });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(TemplatesActions.addTemplateFailure(response.body));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(TemplatesActions.addTemplateSuccess(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(TemplatesActions.addTemplateFailure(error));
  }
}

export function* editTemplateWorkerSaga(action: any): Generator<any, any, any> {
  const {
    template: { templateType: type, ...originalTemplate },
    resolve,
    reject,
  } = action.payload || {};
  try {
    const endpoint = `${monolithApiUrl}${endpoints.editTemplate}`;
    const response = yield call<any>(AuthRequest, {
      endpoint,
      method: 'PATCH',
      body: JSON.stringify({
        [isTemplateEmail(type) ? 'email_template' : 'message_template']: originalTemplate,
      }),
    });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(TemplatesActions.editTemplateFailure(response.body));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      yield put(TemplatesActions.editTemplateSuccess(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(TemplatesActions.editTemplateFailure(error));
  }
}

export function* deleteTemplateWorkerSaga(action: any): Generator<any, any, any> {
  const { itemId, type, resolve, reject } = action.payload || {};
  try {
    if (!itemId) {
      if (typeof reject === 'function') {
        reject();
      }
      yield put(TemplatesActions.deleteTemplateFailure('No id'));
      return;
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.deleteTemplate, { id: itemId })}`;

    const response = yield call(AuthRequest, {
      endpoint,
      method: 'DELETE',
    });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(TemplatesActions.deleteTemplateFailure(response.body));
    } else {
      if (typeof resolve === 'function') {
        resolve(response.body);
      }
      let redirectRoute: string;
      switch (type) {
        case TemplateTypesEnum.Email:
          redirectRoute = routes.experienceManager.emailTemplates;
          break;
        case TemplateTypesEnum.Note:
          redirectRoute = routes.experienceManager.customMessages;
          break;
        case TemplateTypesEnum.ClaimLink:
          redirectRoute = routes.experienceManager.claimLink;
          break;
        default:
          redirectRoute = routes.templates.root;
      }
      yield put(routerProxyPush(redirectRoute));
      yield put(TemplatesActions.deleteTemplateSuccess({ itemId }));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(TemplatesActions.deleteTemplateFailure(error));
  }
}

export function* addClaimLinkTemplateWorkerSaga(
  action: IReduxAction<ISaveClaimLinkTemplateRequest>,
): Generator<any, any, any> {
  const { template, resolve, reject } = action.payload || {};

  yield put(TemplatesActions.setClaimLinkTemplateSaving(true));

  try {
    if (!template) {
      throw new Error('Template data is required');
    }

    const endpoint = `${monolithApiUrl}${endpoints.addClaimLinkTemplate}`;

    const response: IResponse<ICreateClaimLinkTemplateResponse | IClaimLinkTemplateErrorResponse | IApiError> =
      yield call(AuthRequest, {
        endpoint,
        method: 'POST',
        body: JSON.stringify(template),
      });

    if (!response.ok) {
      const errorResponse = response.body as IClaimLinkTemplateErrorResponse | IApiError;

      if ('errors' in errorResponse && 'message' in errorResponse && errorResponse.errors) {
        yield put(
          TemplatesActions.setTemplateValidationErrors({
            errors: errorResponse.errors,
            message: errorResponse.message,
          }),
        );
        yield put(TemplatesActions.setTemplateValidationModal(TemplateValidationModalEnum.Error));
      }

      yield put(TemplatesActions.addClaimLinkTemplateFailure(errorResponse as any));
      if (reject) {
        yield call(reject, errorResponse);
      }
    } else {
      yield put(TemplatesActions.clearTemplateValidation());
      const successBody = response.body as ICreateClaimLinkTemplateResponse;
      yield put(TemplatesActions.addClaimLinkTemplateSuccess(successBody));
      if (resolve) {
        yield call(resolve, successBody);
      }
    }
  } catch (error) {
    yield put(TemplatesActions.addClaimLinkTemplateFailure(error));
    if (reject) {
      yield call(reject, error);
    }
  } finally {
    yield put(TemplatesActions.setClaimLinkTemplateSaving(false));
  }
}

function* editClaimLinkTemplateWorkerSaga(
  action: IReduxAction<ISaveClaimLinkTemplateRequest>,
): Generator<any, any, any> {
  const { uid, template, resolve, reject } = action.payload || {};

  yield put(TemplatesActions.setClaimLinkTemplateSaving(true));

  try {
    if (!uid) {
      throw new Error('Template ID is required for editing');
    }

    if (!template) {
      throw new Error('Template data is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.editClaimLinkTemplate, { id: uid })}`;

    const response: IResponse<IClaimLinkTemplate | IClaimLinkTemplateErrorResponse | IApiError> = yield call(
      AuthRequest,
      {
        endpoint,
        method: 'PUT',
        body: JSON.stringify(template),
      },
    );

    if (!response.ok) {
      const errorResponse = response.body as IClaimLinkTemplateErrorResponse | IApiError;

      if ('errors' in errorResponse && 'message' in errorResponse && errorResponse.errors) {
        yield put(
          TemplatesActions.setTemplateValidationErrors({
            errors: errorResponse.errors,
            message: errorResponse.message,
          }),
        );
        yield put(TemplatesActions.setTemplateValidationModal(TemplateValidationModalEnum.Error));
      }

      yield put(TemplatesActions.editClaimLinkTemplateFailure(errorResponse as any));
      if (reject) {
        yield call(reject, errorResponse);
      }
    } else {
      yield put(TemplatesActions.clearTemplateValidation());
      const successBody = response.body as IClaimLinkTemplate;
      yield put(TemplatesActions.editClaimLinkTemplateSuccess(successBody));
      if (resolve) {
        yield call(resolve, successBody);
      }
    }
  } catch (error) {
    yield put(TemplatesActions.editClaimLinkTemplateFailure(error));
    if (reject) {
      yield call(reject, error);
    }
  } finally {
    yield put(TemplatesActions.setClaimLinkTemplateSaving(false));
  }
}

function* syncBlueprintToTemplateDraftWorkerSaga(
  action: IReduxAction<Partial<IClaimLinkTemplate> | null>,
): Generator<any, any, any> {
  const blueprint = action.payload;
  const claimLinks: ReturnType<typeof selectClaimLinks> = yield select(selectClaimLinks);
  const organizationLogoUrl: string = yield select(selectCurrentOrganizationLogoUrl);

  // Only update if there's an active draft (the draft selector falls back to initial
  // values, so read the raw slice — a sync with no open builder must not fabricate one)
  const currentDraft: IClaimLinkBuilderValues | null = claimLinks?.draft ?? null;
  if (!currentDraft) {
    return;
  }

  // Generate what the global values SHOULD be from the new blueprint
  const blueprintDefaults = getInitialTemplateBuilderValues(blueprint, organizationLogoUrl);

  // Propagate new globalLogoImageUrl to pages that are still using the old default
  const oldGlobal = currentDraft.globalLogoImageUrl;
  const newGlobal = blueprintDefaults.globalLogoImageUrl;
  const syncedPages = { ...currentDraft.pages };
  (Object.keys(syncedPages) as (keyof typeof syncedPages)[]).forEach((pageId) => {
    const page = syncedPages[pageId];
    if (page.overrides.logoImageUrl === oldGlobal) {
      syncedPages[pageId] = {
        ...page,
        overrides: { ...page.overrides, logoImageUrl: newGlobal },
      };
    }
  });

  // Update ONLY global default fields from blueprint, preserve everything else
  const syncedDraft: IClaimLinkBuilderValues = {
    ...currentDraft, // Start with current state

    // Update ONLY global default fields from blueprint
    globalLogoImageUrl: newGlobal,
    globalLogoImageHref: blueprintDefaults.globalLogoImageHref,
    globalAppBarBackgroundColor: blueprintDefaults.globalAppBarBackgroundColor,
    globalHeaderMediaUrl: blueprintDefaults.globalHeaderMediaUrl,
    globalHeroSectionBackgroundColor: blueprintDefaults.globalHeroSectionBackgroundColor,
    globalHeroSectionImageUrl: blueprintDefaults.globalHeroSectionImageUrl,
    globalHeroSectionImagePosition: blueprintDefaults.globalHeroSectionImagePosition,
    globalHeroSectionImageFit: blueprintDefaults.globalHeroSectionImageFit,
    globalHeroSectionBackgroundType: blueprintDefaults.globalHeroSectionBackgroundType,
    globalAccentColor: blueprintDefaults.globalAccentColor,
    globalButtonRadius: blueprintDefaults.globalButtonRadius,
    backgroundColor: blueprintDefaults.backgroundColor,

    pages: syncedPages,
  };

  yield put(TemplatesActions.setClaimLinkBuilderDraft(syncedDraft));
}

const sagas = {
  *watchEmailsTemplatesRequest() {
    yield takeLatest(TemplatesActions.FETCH_EMAILS_TEMPLATES_REQUEST, emailsTemplatesWorkerSaga);
  },
  *watchNotesTemplatesRequest() {
    yield takeLatest(TemplatesActions.FETCH_NOTES_TEMPLATES_REQUEST, notesTemplatesWorkerSaga);
  },
  *watchAddTemplateRequest() {
    yield takeLatest(TemplatesActions.ADD_TEMPLATE_REQUEST, addTemplateWorkerSaga);
  },
  *watchEditTemplateRequest() {
    yield takeLatest(TemplatesActions.EDIT_TEMPLATE_REQUEST, editTemplateWorkerSaga);
  },
  *watchDeleteTemplateRequest() {
    yield takeLatest(TemplatesActions.DELETE_TEMPLATE_REQUEST, deleteTemplateWorkerSaga);
  },
  *watchClaimLinkTemplatesRequest() {
    yield takeLatest(TemplatesActions.FETCH_CLAIM_LINK_TEMPLATES_REQUEST, claimLinkTemplatesWorkerSaga);
  },
  *watchUpdateClaimLinkTemplateDetailsRequest() {
    yield takeLatest(
      TemplatesActions.UPDATE_CLAIM_LINK_TEMPLATE_DETAILS_REQUEST,
      updateClaimLinkTemplateDetailsWorkerSaga,
    );
  },
  *watchSyncBlueprintToTemplateDraft() {
    yield takeLatest(TemplatesActions.SYNC_BLUEPRINT_TO_TEMPLATE_DRAFT, syncBlueprintToTemplateDraftWorkerSaga);
  },
  *watchAddClaimLinkTemplateRequest() {
    yield takeLatest(TemplatesActions.ADD_CLAIM_LINK_TEMPLATE_REQUEST, addClaimLinkTemplateWorkerSaga);
  },
  *watchEditClaimLinkTemplateRequest() {
    yield takeLatest(TemplatesActions.EDIT_CLAIM_LINK_TEMPLATE_REQUEST, editClaimLinkTemplateWorkerSaga);
  },
  *watchFetchClaimLinkTemplateByIdRequest() {
    yield takeLatest(TemplatesActions.FETCH_CLAIM_LINK_TEMPLATE_BY_ID_REQUEST, fetchClaimLinkTemplateByIdWorkerSaga);
  },
};

export default sagas;
